import { Head, router } from '@inertiajs/react'
import { FormEvent, ReactNode, useCallback, useState } from 'react'
import {
  HiOutlineInformationCircle,
  HiOutlinePlus,
  HiOutlineSparkles,
  HiOutlineXMark,
} from 'react-icons/hi2'
import AppLayout from '../../components/AppLayout'
import IconPicker from '../../components/IconPicker'
import InfoDialog from '../../components/InfoDialog'
import PageSubHeader from '../../components/PageSubHeader'
import ScoringExplainer from '../../components/ScoringExplainer'
import SortableList from '../../components/SortableList'

type QuestionType = 'single' | 'multi'

type OptionRow = {
  id: string // stable client key (string form of db id or "new-xxx")
  db_id: number | null
  label: string
  description: string
  icon: string
  score: number
}

type Template = {
  id: number | null
  text: string
  description: string
  question_type: QuestionType
  options: Array<{ id: number | null; label: string; description: string; icon: string; score: number }>
}

type Suggestion = {
  label: string
  score: number
  reason: string
}

type SuggestResponse = {
  won_leads: number
  total_leads: number
  events: number
  suggestions: Suggestion[]
  insights: string[]
  summary: string
}

type Props = {
  template: Template
  errors: Record<string, string[] | undefined>
}

let tempCounter = 0
const nextTempId = () => `new-${++tempCounter}`

export default function EditQuestionTemplate({ template, errors }: Props) {
  const isNew = template.id === null
  const [text, setText] = useState(template.text)
  const [description, setDescription] = useState(template.description)
  const [questionType, setQuestionType] = useState<QuestionType>(template.question_type)
  const [options, setOptions] = useState<OptionRow[]>(
    template.options.length > 0
      ? template.options.map((o) => ({
          id: o.id ? `db-${o.id}` : nextTempId(),
          db_id: o.id,
          label: o.label,
          description: o.description,
          icon: o.icon,
          score: o.score,
        }))
      : [{ id: nextTempId(), db_id: null, label: '', description: '', icon: '', score: 0 }],
  )
  const [processing, setProcessing] = useState(false)
  const [scoringInfoOpen, setScoringInfoOpen] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [suggestion, setSuggestion] = useState<SuggestResponse | null>(null)
  const [suggestError, setSuggestError] = useState<string | null>(null)

  const fetchSuggestions = useCallback(async () => {
    if (!template.id) return
    setSuggesting(true)
    setSuggestError(null)
    setSuggestion(null)
    try {
      const res = await fetch(`/question_templates/${template.id}/suggest_scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
      })
      const data = await res.json()
      if (!res.ok) {
        setSuggestError(data.error || 'Something went wrong')
      } else {
        setSuggestion(data)
      }
    } catch {
      setSuggestError('Network error — please try again')
    } finally {
      setSuggesting(false)
    }
  }, [template.id])

  const applySuggestions = () => {
    if (!suggestion) return
    setOptions((list) =>
      list.map((opt) => {
        const match = suggestion.suggestions.find((s) => s.label === opt.label)
        return match ? { ...opt, score: match.score } : opt
      }),
    )
    setSuggestion(null)
  }

  const addOption = () =>
    setOptions((list) => [
      ...list,
      { id: nextTempId(), db_id: null, label: '', description: '', icon: '', score: 0 },
    ])

  const removeOption = (id: string) =>
    setOptions((list) => list.filter((o) => o.id !== id))

  const updateOption = (id: string, patch: Partial<OptionRow>) =>
    setOptions((list) => list.map((o) => (o.id === id ? { ...o, ...patch } : o)))

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessing(true)
    const payload = {
      question_template: {
        text,
        description,
        question_type: questionType,
        options: options.map((o) => ({
          id: o.db_id,
          label: o.label,
          description: o.description,
          icon: o.icon,
          score: o.score,
        })),
      },
    }
    if (isNew) {
      router.post('/question_templates', payload, { onFinish: () => setProcessing(false) })
    } else {
      router.patch(`/question_templates/${template.id}`, payload, {
        onFinish: () => setProcessing(false),
      })
    }
  }

  return (
    <>
      <Head title={isNew ? 'New Template' : `Edit ${template.text || 'Template'}`} />
      <AppLayout
        active="question_templates"
        hideTabs
        subHeader={
          <PageSubHeader
            backHref="/question_templates"
            backLabel="Back to templates"
            title={isNew ? 'New Question Template' : 'Edit Question Template'}
          />
        }
      >
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-[903px] space-y-6">
          <Card title="Question">
            <div className="grid grid-cols-1 gap-4">
              <Field label="Question text" error={errors.text}>
                <input
                  type="text"
                  required
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className={inputClass(errors.text)}
                />
              </Field>
              <Field label="Description" error={errors.description}>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClass()}
                />
              </Field>
              <Field label="Type" error={errors.question_type}>
                <select
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value as QuestionType)}
                  className={inputClass()}
                >
                  <option value="single">Single choice</option>
                  <option value="multi">Multi choice</option>
                </select>
              </Field>
            </div>
          </Card>

          <Card
            title="Options"
            trailing={
              <div className="flex items-center gap-2">
                {!isNew && (
                  <button
                    type="button"
                    onClick={fetchSuggestions}
                    disabled={suggesting}
                    className="flex items-center gap-1 rounded-md bg-midnight-green/10 px-2.5 py-1 font-display text-xs font-medium text-midnight-green transition hover:bg-midnight-green/20 disabled:opacity-50"
                  >
                    <HiOutlineSparkles className="h-3.5 w-3.5" />
                    {suggesting ? 'Analyzing…' : 'Suggest scores from win data'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setScoringInfoOpen(true)}
                  className="flex items-center gap-1 rounded-md px-2 py-1 font-display text-xs font-medium text-black/40 transition hover:bg-gray-100 hover:text-black/60"
                >
                  <HiOutlineInformationCircle className="h-4 w-4" />
                  How scoring works
                </button>
              </div>
            }
          >
            <SortableList items={options} onReorder={setOptions} size="xs" renderItem={(opt, { dragHandle, moveControls }) => (
              <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex flex-col items-center gap-1 pt-2">
                  {dragHandle}
                  {moveControls}
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <input
                    type="text"
                    placeholder="Label"
                    value={opt.label}
                    onChange={(e) => updateOption(opt.id, { label: e.target.value })}
                    className={inputClass()}
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={opt.description}
                    onChange={(e) => updateOption(opt.id, { description: e.target.value })}
                    className={inputClass()}
                  />
                  <div className="flex items-center gap-3">
                    <IconPicker
                      value={opt.icon}
                      onChange={(icon) => updateOption(opt.id, { icon })}
                    />
                    <span className="font-display text-xs text-black/40">
                      {!opt.icon && 'No icon selected'}
                    </span>
                    <div className="ml-auto flex items-center gap-1.5">
                      <span className="font-display text-xs text-black/40">Score</span>
                      <input
                        type="number"
                        min={0}
                        value={opt.score}
                        onChange={(e) => updateOption(opt.id, { score: parseInt(e.target.value) || 0 })}
                        className="w-16 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-center font-display text-sm text-ink focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
                      />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeOption(opt.id)}
                  className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                  aria-label="Remove option"
                >
                  <HiOutlineXMark className="h-5 w-5" />
                </button>
              </div>
            )} />
            <button
              type="button"
              onClick={addOption}
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-white px-4 py-2 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
            >
              <HiOutlinePlus className="h-4 w-4" />
              Add option
            </button>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <a
              href="/question_templates"
              className="rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="rounded-md bg-ink px-7 py-3 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green disabled:cursor-not-allowed disabled:opacity-60"
            >
              {processing ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </AppLayout>

      <InfoDialog size="lg" open={scoringInfoOpen} title="How lead scoring works" onClose={() => setScoringInfoOpen(false)}>
        <ScoringExplainer />
      </InfoDialog>

      <InfoDialog
        open={suggestion !== null || suggestError !== null}
        size="lg"
        title={suggestError ? 'Could not suggest scores' : 'AI Score Suggestions'}
        onClose={() => { setSuggestion(null); setSuggestError(null) }}
      >
        {suggestError ? (
          <p className="text-rose-600">{suggestError}</p>
        ) : suggestion ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-600">
                {suggestion.events} event{suggestion.events === 1 ? '' : 's'}
              </span>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-600">
                {suggestion.total_leads} total leads
              </span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                {suggestion.won_leads} won
              </span>
            </div>

            {suggestion.won_leads < 5 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Low sample size ({suggestion.won_leads} won leads). Suggestions may be unreliable.
              </div>
            )}

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium text-black/40">
                  <th className="pb-2">Option</th>
                  <th className="pb-2 text-center">Current</th>
                  <th className="pb-2 text-center">Suggested</th>
                  <th className="pb-2">Why</th>
                </tr>
              </thead>
              <tbody>
                {suggestion.suggestions.map((s) => {
                  const current = options.find((o) => o.label === s.label)?.score ?? 0
                  const changed = current !== s.score
                  return (
                    <tr key={s.label} className="border-b border-gray-100">
                      <td className="py-2 pr-2 font-medium text-ink">{s.label}</td>
                      <td className="py-2 text-center text-black/40">{current}</td>
                      <td className={`py-2 text-center font-semibold ${changed ? 'text-midnight-green' : 'text-black/40'}`}>
                        {s.score}
                      </td>
                      <td className="py-2 pl-2 text-xs text-black/50">{s.reason}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {suggestion.insights && suggestion.insights.length > 0 && (
              <div>
                <h4 className="mb-1.5 font-display text-xs font-semibold text-ink">Insights</h4>
                <ul className="space-y-1">
                  {suggestion.insights.map((insight, i) => (
                    <li key={i} className="flex gap-2 text-xs text-black/60">
                      <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-midnight-green/50" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-black/50">{suggestion.summary}</p>

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 font-display text-sm font-medium text-ink transition hover:bg-gray-50"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={applySuggestions}
                className="rounded-md bg-ink px-4 py-2 font-display text-sm font-medium text-white transition hover:bg-midnight-green"
              >
                Apply Suggestions
              </button>
            </div>
          </div>
        ) : null}
      </InfoDialog>
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Helpers — kept inline; shared patterns with Leads/Edit.tsx but not
// extracted yet (second-caller rule).
// ────────────────────────────────────────────────────────────────

function Card({ title, trailing, children }: { title: string; trailing?: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between gap-4 px-6 pt-5">
        <h2 className="font-display text-lg font-medium text-ink">{title}</h2>
        {trailing}
      </div>
      <div className="p-6 pt-4">{children}</div>
    </div>
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string[]
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-display text-base font-medium text-black/60">{label}</span>
      {children}
      {error && error.length > 0 && (
        <span className="mt-1 font-display text-xs text-rose-600">{error.join(', ')}</span>
      )}
    </label>
  )
}

function inputClass(error?: string[]) {
  const base =
    'w-full rounded-md border bg-white px-3 py-3 font-display text-base text-ink shadow-sm placeholder:text-black/40 focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green'
  return `${base} ${error && error.length > 0 ? 'border-rose-400' : 'border-gray-300'}`
}
